'use client';
import { useCallback, useEffect, useState } from 'react';
import { ButtonIcon } from '@workspace/ui/common';
import { NavigationBar, HeaderSearch, Logo, PrimaryButton } from './ui';
import { MobileMegaMenu } from '../MegaMenu/Mobile/MobileMegaMenu';
import { usePathname } from 'next/navigation';
import { useHeader } from './hooks';
import { cn } from '@workspace/lib';

export interface HeaderProps {
  className?: string;
  onActivePathChange?: (newPath: string) => void;
}

export const Header = ({ className = '', onActivePathChange }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const [activePath, setActivePath] = useState(pathname);
  const { headerData, loading } = useHeader();

  useEffect(() => {
    setActivePath(pathname);
  }, [pathname]);

  // Lock body scroll and prevent background scroll when mobile menu is open
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const restoreY = window.scrollY || window.pageYOffset || 0;

    Object.assign(document.body.style, {
      position: 'fixed',
      top: `-${restoreY}px`,
      left: '0',
      right: '0',
      width: '100%',
      overflow: 'hidden',
    });

    const preventScroll = (event: Event) => {
      event.preventDefault();
    };

    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      ['overflow', 'position', 'top', 'left', 'right', 'width'].forEach((p) => document.body.style.removeProperty(p));
      window.scrollTo(0, restoreY);
      document.removeEventListener('wheel', preventScroll as EventListener);
      document.removeEventListener('touchmove', preventScroll as EventListener);
    };
  }, [isMobileMenuOpen]);

  const handleMobileMenuClose = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const onSearchToggle = useCallback((isOpen: boolean) => {
    if (isOpen) {
      setIsMobileMenuOpen(false);
    }
  }, []);

  return (
    <header
      className={cn('bg-white sticky top-0 z-[70] h-21.25 max-md:h-18 border-b border-b-secondary-100', className)}
    >
      <div className="porta-container p-md md:px-4xl relative h-full border-b border-b-secondary-100 sm:border-b-0 z-[70]">
        <div className="flex items-center justify-between h-full">
          <div
            className={cn('flex-shrink-0', {
              'animate-pulse h-12.5 w-37.5 bg-gray-50 text-transparent rounded-md': loading,
            })}
          >
            <Logo headerData={headerData} />
          </div>

          <div className="right-header-container xl:flex justify-between items-center gap-sm hidden">
            <HeaderSearch onSearchToggle={onSearchToggle} />

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center space-x-lg ml-sm -mb-px">
              <NavigationBar
                loading={loading}
                navigationItems={headerData?.navigationItems || []}
                activePath={activePath}
                onActivePathChange={onActivePathChange}
              />
            </div>

            {/* Search and Primary Button */}
            <div className="hidden xl:flex items-center space-x-md">
              <PrimaryButton primaryButton={headerData?.primaryButton} />
            </div>
          </div>

          {/* Mobile Header */}
          <div className="xl:hidden flex items-center justify-end w-full">
            {/* Right side - Search and Menu */}
            <div className="flex items-center gap-1.5 bg-transparent">
              {/* Search */}
              <HeaderSearch onSearchToggle={onSearchToggle} />

              {/* Hamburger Menu / Close Button */}
              <ButtonIcon
                iconName={isMobileMenuOpen ? 'cancelIcon' : 'HamburgerIcon'}
                iconSize={isMobileMenuOpen ? 18 : 24}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-secondary-400 hover:text-primary-500 transition-colors duration-200 active:border-none size-6"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      <MobileMegaMenu
        isOpen={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
        activePath={activePath}
        onActivePathChange={onActivePathChange}
        navigationItems={headerData?.navigationItems}
        primaryButton={headerData?.primaryButton}
      />
    </header>
  );
};
